import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { AuthService } from '../../../core/auth/auth.service';
import { BOOKING_ACTION_ACCESS, canAccess } from '../../../core/auth/access-control';
import { canModifyBooking as canModifyBookingAccess } from '../../../core/auth/booking-access';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { COUNTRY_OPTIONS } from '../../../shared/constants/countries.constant';
import { CustomerService } from '../../customers/services/customer.service';
import { TourItem, TourService } from '../../tours/services/tour.service';
import {
  GroupBookingListItem,
  GroupBookingService,
  SaveGroupBookingRequest,
} from '../services/group-booking.service';

interface BookingRow {
  id: string;
  code: string;
  tripName: string;
  tourCode: string | null;
  country: string;
  countryName: string;
  customerName: string;
  customerCode: string;
  total: number;
  bookingStatus: GroupBookingListItem['status'];
  paymentStatus: GroupBookingListItem['paymentStatus'];
  travelDate: string;
  returnDate: string | null;
  customerId: string;
  travelerCount: number;
  tourPrice: number;
  discount: number;
  hasPayments: boolean;
}

@Component({
  selector: 'app-group-bookings-page',
  imports: [
    DatePipe,
    DecimalPipe,
    ReactiveFormsModule,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
    TableModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    InputNumberModule,
  ],
  templateUrl: './group-bookings-page.component.html',
})
export class GroupBookingsPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly overlay = inject(GlobalOverlayService);
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly groupBookingService = inject(GroupBookingService);
  private readonly tourService = inject(TourService);
  private readonly auth = inject(AuthService);

  readonly canManageBooking = computed(() =>
    canAccess(this.auth.user(), BOOKING_ACTION_ACCESS.manage),
  );

  readonly bookings = signal<BookingRow[]>([]);
  readonly isBookingModalOpen = signal(false);
  readonly isLoadingBookings = signal(false);
  readonly isSavingGroupBooking = signal(false);
  readonly editingBookingId = signal<string | null>(null);
  readonly customerOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly countryOptions = COUNTRY_OPTIONS.map((country) => ({
    label: country.label,
    value: country.label,
  }));
  private readonly groupTours = signal<TourItem[]>([]);
  readonly tourCodeOptions = computed(() => {
    const editingBookingId = this.editingBookingId();

    return this.groupTours()
      .filter((tour) => {
        const activeGroupBookings = tour.bookings.filter(
          (booking) =>
            booking.bookingType === 'GROUP_TOUR' &&
            !['DELETED', 'REFUND'].includes(booking.status),
        );

        return (
          activeGroupBookings.length === 0 ||
          (!!editingBookingId &&
            activeGroupBookings.every((booking) => booking.id === editingBookingId))
        );
      })
      .map((tour) => ({
        id: tour.id,
        label: `${tour.tourCode} · ${tour.tourName}`,
        value: tour.tourCode,
      }));
  });
  readonly selectedTour = signal<TourItem | null>(null);
  readonly isLoadingTour = signal(false);

  ngOnInit(): void {
    void this.loadGroupBookingPageData();
  }

  async loadGroupBookingPageData(): Promise<void> {
    if (this.isLoadingBookings()) return;
    this.isLoadingBookings.set(true);
    this.overlay.showLoading('กำลังโหลดข้อมูล รายการ...');
    try {
      const [customers, bookings, tours] = await Promise.all([
        this.canManageBooking() ? this.customerService.getActiveOptions() : Promise.resolve([]),
        this.groupBookingService.getGroupBookings(),
        this.tourService.getAll(),
      ]);
      this.customerOptions.set(
        customers.map((customer) => ({ label: customer.name, value: customer.id })),
      );
      this.groupTours.set(
        tours
          .filter(
            (tour) =>
              tour.tourType === 'GROUP_TOUR' &&
              !['DELETED', 'CANCELLED', 'COMPLETED'].includes(tour.status),
          ),
      );
      this.bookings.set(
        bookings.map((booking) => ({
          id: booking.id,
          code: booking.bookingNo,
          tripName: booking.tourName,
          tourCode: booking.tourCode,
          country:
            COUNTRY_OPTIONS.find((country) => country.value === booking.country)?.label ??
            booking.country ??
            '',
          countryName:
            COUNTRY_OPTIONS.find((country) => country.value === booking.country)?.label ??
            booking.country ??
            '-',
          customerName: booking.booking_customer.name,
          customerCode: booking.booking_customer.customerCode,
          total: Number(booking.totalAmount),
          bookingStatus: booking.status,
          paymentStatus: booking.paymentStatus,
          travelDate: booking.departureDate,
          returnDate: booking.returnDate,
          customerId: booking.booking_customer.id,
          travelerCount: booking.pax,
          tourPrice: Number(booking.pricePerPerson),
          discount: Number(booking.discount ?? 0),
          hasPayments: booking.booking_payments.length > 0,
        })),
      );
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดข้อมูล Booking ไม่สำเร็จ', message);
    } finally {
      this.isLoadingBookings.set(false);
      this.overlay.hideLoading();
    }
  }

  readonly bookingForm = this.fb.group(
    {
      customerId: ['', Validators.required],
      customerIsTraveler: [false],
      tourCode: ['', Validators.required],
      programName: ['', Validators.required],
      country: ['', Validators.required],
      departureDate: [null as Date | null, Validators.required],
      returnDate: [null as Date | null, Validators.required],
      travelerCount: [1, [Validators.required, Validators.min(1)]],
      tourPrice: [null as number | null, [Validators.required, Validators.min(0)]],
      discount: [0 as number | null, Validators.min(0)],
    },
    { validators: this.bookingLimitValidator() },
  );

  private bookingLimitValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const travelerCount = Number(control.get('travelerCount')?.value);
      const tourPrice = Number(control.get('tourPrice')?.value);
      const discount = Number(control.get('discount')?.value ?? 0);
      if (!Number.isFinite(travelerCount) || !Number.isFinite(tourPrice) || !Number.isFinite(discount)) {
        return null;
      }
      return discount > tourPrice * travelerCount ? { discountExceeded: true } : null;
    };
  }

  openGroupBookingModal(): void {
    this.editingBookingId.set(null);
    this.bookingForm.reset({
      travelerCount: 1,
      customerIsTraveler: false,
    });
    this.selectedTour.set(null);
    this.isBookingModalOpen.set(true);
  }

  async onGroupTourSelected(tourCode: string | null): Promise<void> {
    this.selectedTour.set(null);
    if (!tourCode) return;
    const option = this.tourCodeOptions().find((item) => item.value === tourCode);
    if (!option) return;
    this.isLoadingTour.set(true);
    try {
      const tour = await this.tourService.getById(option.id);
      this.selectedTour.set(tour);
      this.bookingForm.patchValue({
        programName: tour.tourName,
        country: tour.country,
        departureDate: this.parseDate(tour.travelDate),
        returnDate: tour.returnDate ? this.parseDate(tour.returnDate) : null,
        tourPrice: Number(tour.tourprice),
      });
      this.bookingForm.updateValueAndValidity();
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'ไม่สามารถโหลดข้อมูลทัวร์ได้';
      this.overlay.error('โหลดข้อมูลทัวร์ไม่สำเร็จ', message);
    } finally {
      this.isLoadingTour.set(false);
    }
  }

  openEditGroupBookingModal(booking: BookingRow): void {
    if (booking.bookingStatus === 'DELETED' || !this.canModifyGroupBooking(booking)) return;
    if (!booking.tourCode?.trim()) {
      this.overlay.error(
        'ไม่สามารถแก้ไข Booking ได้',
        'ไม่พบรหัสทัวร์ของ Booking นี้ กรุณาตรวจสอบข้อมูลทัวร์ก่อนแก้ไข',
      );
      return;
    }
    const tourCode = booking.tourCode.trim();
    this.editingBookingId.set(booking.id);
    this.bookingForm.reset({
      customerId: booking.customerId,
      customerIsTraveler: false,
      tourCode,
      programName: booking.tripName,
      country: booking.country,
      departureDate: this.parseDate(booking.travelDate),
      returnDate: booking.returnDate ? this.parseDate(booking.returnDate) : null,
      travelerCount: booking.travelerCount,
      tourPrice: booking.tourPrice,
      discount: booking.discount,
    });
    void this.onGroupTourSelected(tourCode);
    this.isBookingModalOpen.set(true);
  }

  closeGroupBookingModal(): void {
    this.isBookingModalOpen.set(false);
    this.editingBookingId.set(null);
  }

  isInvalid(controlName: keyof typeof this.bookingForm.controls): boolean {
    const control = this.bookingForm.controls[controlName];
    return control.touched && control.invalid;
  }

  async saveGroupBooking(): Promise<void> {
    if (this.isLoadingTour()) return;
    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      return;
    }
    const selectedTour = this.selectedTour();
    if (!selectedTour) {
      this.bookingForm.controls.tourCode.markAsTouched();
      this.overlay.error('กรุณาเลือกทัวร์', 'ไม่พบข้อมูลทัวร์ที่เลือก กรุณาเลือกทัวร์อีกครั้ง');
      return;
    }
    if (this.groupTourCapacityExceeded()) {
      this.bookingForm.controls.travelerCount.markAsTouched();
      return;
    }

    const { departureDate, returnDate } = this.bookingForm.getRawValue();
    if (departureDate && returnDate && returnDate < departureDate) {
      this.overlay.error('วันที่เดินทางไม่ถูกต้อง', 'วันเดินทางกลับต้องไม่น้อยกว่าวันเดินทางไป');
      return;
    }

    const formValue = this.bookingForm.getRawValue();
    const editingBookingId = this.editingBookingId();
    const request: SaveGroupBookingRequest = {
      tourId: selectedTour.id,
      customerId: formValue.customerId!,
      customerIsTraveler: formValue.customerIsTraveler ?? false,
      tourName: formValue.programName!,
      tourCode: formValue.tourCode!,
      country: formValue.country!,
      departureDate: this.formatDate(formValue.departureDate!),
      returnDate: this.formatDate(formValue.returnDate!),
      pax: formValue.travelerCount!,
      tourPrice: formValue.tourPrice!,
      discount: formValue.discount ?? 0,
    };

    this.isSavingGroupBooking.set(true);
    this.overlay.showLoading(
      editingBookingId ? 'กำลังแก้ไข Booking เหมากรุ๊ป...' : 'กำลังสร้าง Booking เหมากรุ๊ป...',
    );
    try {
      const savedBooking = editingBookingId
        ? await this.groupBookingService.updateGroupBooking(editingBookingId, request)
        : await this.groupBookingService.createGroupBooking(request);
      this.closeGroupBookingModal();
      if (editingBookingId) {
        await this.loadGroupBookingPageData();
      }
      this.overlay.success(
        editingBookingId ? 'แก้ไข Booking เหมากรุ๊ปสำเร็จ' : 'สร้าง Booking เหมากรุ๊ปสำเร็จ',
        `บันทึก ${savedBooking.bookingNo} เรียบร้อยแล้ว`,
      );
      if (!editingBookingId) {
        await this.router.navigate(['/bookings', savedBooking.id], {
          queryParams: { from: 'group-bookings' },
        });
      }
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึก Booking เหมากรุ๊ปไม่สำเร็จ', message);
    } finally {
      this.isSavingGroupBooking.set(false);
      this.overlay.hideLoading();
    }
  }

  groupTourRemainingPax(): number | null {
    const tour = this.selectedTour();
    if (!tour || tour.requiredPax === null) return null;
    const editingBookingId = this.editingBookingId();
    const bookedPax = tour.bookings
      .filter(
        (booking) =>
          booking.id !== editingBookingId &&
          !['DELETED', 'REFUND'].includes(booking.status),
      )
      .reduce((total, booking) => total + booking.pax, 0);
    const currentPax = Number(this.bookingForm.controls.travelerCount.value ?? 0);
    return Math.max(tour.requiredPax - bookedPax - currentPax, 0);
  }

  groupTourCapacityExceeded(): boolean {
    const tour = this.selectedTour();
    if (!tour || tour.requiredPax === null) return false;
    const editingBookingId = this.editingBookingId();
    const bookedPax = tour.bookings
      .filter(
        (booking) =>
          booking.id !== editingBookingId &&
          !['DELETED', 'REFUND'].includes(booking.status),
      )
      .reduce((total, booking) => total + booking.pax, 0);
    return bookedPax + Number(this.bookingForm.controls.travelerCount.value ?? 0) > tour.requiredPax;
  }

  async deleteGroupBooking(booking: BookingRow): Promise<void> {
    if (booking.bookingStatus === 'DELETED' || !this.canModifyGroupBooking(booking)) return;
    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการลบ Booking',
      message: `ต้องการลบ ${booking.code} ใช่หรือไม่? รายการจะถูกเปลี่ยนสถานะเป็นลบแล้ว`,
      confirmText: 'ลบ Booking',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังลบ Booking...');
    try {
      const deletedBooking = await this.groupBookingService.deleteGroupBooking(booking.id);
      this.bookings.update((bookings) =>
        bookings.map((item) =>
          item.id === deletedBooking.id
            ? { ...item, bookingStatus: deletedBooking.status }
            : item,
        ),
      );
      this.overlay.success('ลบ Booking สำเร็จ', 'เปลี่ยนสถานะ Booking เป็นลบแล้ว');
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'ไม่สามารถลบ Booking ได้';
      this.overlay.error('ลบ Booking ไม่สำเร็จ', message);
    }
  }

  private parseDate(value: string): Date {
    return new Date(`${value.slice(0, 10)}T00:00:00`);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  viewGroupBooking(id: string): void {
    void this.router.navigate(['/bookings', id], {
      queryParams: { from: 'group-bookings' },
    });
  }

  canModifyGroupBooking(booking: BookingRow): boolean {
    return canModifyBookingAccess(this.auth.user(), booking.hasPayments);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isBookingModalOpen()) this.closeGroupBookingModal();
  }
}
